import React, { useState, Suspense } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CalendarToday,
  Scale,
  Timeline,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface AnimalNutritionRecord {
  _key: string;
  _id: string;
  _rev: string;
  farm_id: string;
  cattle_tag: string;
  entity_type: string;
  handling_type_code: string;
  operation_lot_id?: string | null;
  handling_date: string;
  handling_time?: string | null;
  performed_by?: string | null;
  notes?: string | null;
  withdrawal_period_days?: number | null;
  next_handling_due?: string | null;
  cost?: number | null;
  handling_details: {
    feed_type: string;
    quantity_kg: number;
  };
  created_at: string;
  updated_at: string;
}

export interface AnimalData {
  entity_type: string;
  _id: string;
  _key: string;
  _rev: string;
  farm_id: string;
  cattle_id: {
    tag: string;
    country_code: string;
    manufacturer_code: number;
    unique_id: string;
  };
  name: string;
  breed_code: string;
  gender: string;
  birth_date?: string | null;
  birth_weight_kg?: number | null;
  birth_farm_id?: string | null;
  current_farm_id: string;
  current_paddock_id?: string | null;
  production_purpose?: string | null;
  current_status: string;
  death_date?: string | null;
  death_reason?: string | null;
  created_at: string;
  updated_at: string;
  historico_nutricao: AnimalNutritionRecord[];
}

export interface AnimalNutritionHistoryData {
  data: AnimalData;
  resumo: string;
  url: string;
}

export interface AnimalNutritionHistoryViewProps {
  animalNutritionData: AnimalNutritionHistoryData;
}

export const AnimalNutritionHistoryView: React.FC<
  AnimalNutritionHistoryViewProps
> = ({ animalNutritionData }) => {
  const { t, ready } = useTranslation('animal');
  const { data } = animalNutritionData;

  const formatDate = (dateString?: string): string => {
    if (!dateString) return t('nutrition_history.data_not_available');
    try {
      // If date is in YYYY-MM-DD format, add time to avoid timezone issues
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      // For other formats, use normal conversion
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch {
      return dateString;
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Extract animal information
  const animalName = data?.name || 'N/A';
  const animalTag = data?.cattle_id?.tag || data?.name || 'N/A';
  const breedCode = data?.breed_code || 'N/A';
  const gender = data?.gender || 'N/A';
  const nutritionHistory = data?.historico_nutricao || [];

  // Pagination calculations
  const totalPages = Math.ceil(nutritionHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = nutritionHistory.slice(startIndex, endIndex);

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Wait for i18n to be ready
  if (!ready) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header com informações do animal */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={`${t('nutrition_history.name')}: ${animalName}`}
                size="small"
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.neutral[100],
                  color: DESIGN_TOKENS.colors.text.primary,
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`${t('nutrition_history.tag')}: ${animalTag}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`${t('nutrition_history.records')}: ${nutritionHistory.length}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            {animalNutritionData.resumo && (
              <Typography
                variant="body2"
                sx={{
                  color: DESIGN_TOKENS.colors.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {animalNutritionData.resumo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('nutrition_history.animal_info')}
            </Typography>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/vaca.png" 
                  alt="Animal" 
                  style={{ width: 24, height: 24, filter: 'brightness(0)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('nutrition_history.name')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {animalName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Timeline
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('nutrition_history.feeding_records')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {nutritionHistory.length}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('nutrition_history.last_record')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {nutritionHistory.length > 0 ? formatDate(nutritionHistory[0]?.handling_date) : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Informações adicionais do animal */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('nutrition_history.animal_details')}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                label={`${t('nutrition_history.breed')}: ${breedCode}`}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`${t('nutrition_history.gender')}: ${gender}`}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              {data?.current_status && (
                <Chip
                  label={`${t('nutrition_history.status')}: ${data.current_status}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Tabela de histórico */}
        {nutritionHistory.length > 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <img 
                  src="/nutricao.svg" 
                  alt="Nutrição" 
                  style={{ width: 24, height: 24 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('nutrition_history.title')} ({nutritionHistory.length})
                </Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CalendarToday sx={{ fontSize: 18 }} />
                          {t('nutrition_history.date')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <img 
                            src="/gestores/nutricao.svg" 
                            alt={t('nutrition_history.feed')} 
                            style={{ width: 18, height: 18 }}
                          />
                          {t('nutrition_history.feed')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Scale sx={{ fontSize: 18 }} />
                          {t('nutrition_history.quantity')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('nutrition_history.lot')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentItems.map((record, index) => (
                      <TableRow key={record._id || index} hover>
                        <TableCell>
                          {formatDate(record.handling_date)}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              textTransform: "capitalize",
                            }}
                          >
                            {record.handling_details.feed_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: DESIGN_TOKENS.colors.organization.farm,
                              }}
                            >
                              {record.handling_details.quantity_kg} kg
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {record.operation_lot_id && (
                            <Chip
                              label={record.operation_lot_id}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              {totalPages > 1 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('nutrition_history.page_info', { 
                          current: currentPage, 
                          total: totalPages, 
                          total_records: nutritionHistory.length 
                        })}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<NavigateBefore />}
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          {t('nutrition_history.previous')}
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                          (page) => (
                            <Button
                              key={page}
                              variant={page === currentPage ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handlePageClick(page)}
                              sx={{ minWidth: 40 }}
                            >
                              {page}
                            </Button>
                          )
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<NavigateNext />}
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          {t('nutrition_history.next')}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  textAlign: "center",
                }}
              >
                <img 
                  src="/gestores/nutricao.svg" 
                  alt={t('nutrition_history.no_records')} 
                  style={{ 
                    width: 64, 
                    height: 64, 
                    opacity: 0.5,
                    marginBottom: 16 
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('nutrition_history.no_records')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('nutrition_history.no_records_description')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default AnimalNutritionHistoryView;
